<template>
  <div class="p-8 max-w-[1200px] mx-auto space-y-8 min-h-screen">
    <!-- Header -->
    <div class="space-y-2">
      <h1 class="text-3xl font-black tracking-tight text-foreground flex items-center gap-3">
        <CheckSquare class="w-8 h-8 text-muted-foreground" />
        Actions requises
      </h1>
      <p class="text-muted-foreground font-medium italic max-w-2xl">
        Traitez ici tous vos mouvements prévisionnels et abonnements dont la date d'échéance ou d'alerte est atteinte.
      </p>
    </div>

    <!-- Valuation Alerts -->
    <div v-if="valuationAlerts.length > 0" class="space-y-3">
      <div v-for="inv in valuationAlerts" :key="inv.id" 
           class="flex items-center justify-between gap-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl shadow-lg shadow-black/10 hover:shadow-black/20 transition-all group">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
            <History class="w-6 h-6" />
          </div>
          <div class="space-y-0.5">
            <h4 class="font-black text-amber-200">Mise à jour requise : {{ inv.name }}</h4>
            <p class="text-xs text-amber-400/70 font-bold uppercase tracking-tight">
              Dernière valorisation : {{ inv.lastValuationUpdate ? formatDateFr(inv.lastValuationUpdate) : 'Jamais' }}
            </p>
          </div>
        </div>
        <Button size="sm" variant="finance" class="bg-amber-600 hover:bg-amber-700 text-white border-none shadow-xl shadow-amber-950/20" 
                @click="openValuationModal(inv)"
                :aria-label="`Actualiser la valorisation pour ${inv.name}`">
          Actualiser <ArrowRight class="w-3.5 h-3.5 ml-1.5" aria-hidden="true" />
        </Button>
      </div>
    </div>

    <!-- Subscriptions Alerts -->
    <div v-if="subscriptionAlerts.length > 0" class="space-y-3">
      <div v-for="sub in subscriptionAlerts" :key="sub.id" 
           class="flex items-center justify-between gap-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl shadow-lg shadow-black/10 hover:shadow-black/20 transition-all group">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform">
            <Bell class="w-6 h-6" />
          </div>
          <div class="space-y-0.5">
            <h4 class="font-black text-indigo-800 dark:text-indigo-200">Contrôler l'abonnement : {{ sub.name }}</h4>
            <div v-if="sub.comment" class="bg-indigo-500/10 dark:bg-indigo-950/30 p-1.5 rounded-md text-xs text-indigo-800/80 dark:text-indigo-300/80 italic line-clamp-2 mt-1 mb-1.5">
              {{ sub.comment }}
            </div>
            <p v-if="sub.alertDate" class="text-xs text-indigo-600/70 dark:text-indigo-400/70 font-bold uppercase tracking-tight">
              Renouvellement prévu le : {{ formatDateFr(sub.renewalDate) }}
            </p>
          </div>
        </div>
        <Button size="sm" variant="finance" class="bg-indigo-600 hover:bg-indigo-700 text-white border-none shadow-xl shadow-indigo-950/20" 
                @click="validateSubscription(sub.id)" :disabled="validatingId === 'sub_' + sub.id"
                :aria-label="`Valider le contrôle de ${sub.name}`">
          <RefreshCw v-if="validatingId === 'sub_' + sub.id" class="w-3.5 h-3.5 animate-spin mr-1.5" aria-hidden="true" />
          <Check v-else class="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
          Valider
        </Button>
      </div>
    </div>

    <!-- Movements Section -->
    <div class="space-y-6">
      <div v-if="loading" class="flex flex-col items-center justify-center p-20 space-y-4" aria-busy="true">
        <RefreshCw class="w-10 h-10 text-muted-foreground/30 animate-spin" aria-hidden="true" />
        <span class="text-muted-foreground font-bold italic tracking-widest text-xs uppercase">Analyse des flux...</span>
      </div>

      <div v-else-if="groups.length === 0" class="flex flex-col items-center justify-center p-16 bg-card border-2 border-dashed border-border rounded-3xl text-center space-y-4 group shadow-xl shadow-black/20">
        <div class="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
          <Smile class="w-10 h-10" />
        </div>
        <div class="space-y-1">
          <h3 class="text-xl font-black text-foreground">Vous êtes à jour !</h3>
          <p class="text-muted-foreground max-w-sm italic">Aucun mouvement prévisionnel en retard n'est à valider pour le moment.</p>
        </div>
      </div>

      <div v-else class="bg-card border border-border rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
        <Table>
          <TableHeader class="bg-muted/30">
            <TableRow class="border-border hover:bg-transparent">
              <TableHead class="w-[300px] text-[10px] font-black uppercase tracking-widest text-muted-foreground">Placement / Date</TableHead>
              <TableHead class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Type & Catégorie</TableHead>
              <TableHead class="text-[10px] font-black uppercase tracking-widest text-right text-muted-foreground">Montant</TableHead>
              <TableHead class="w-[180px] text-[10px] font-black uppercase tracking-widest text-right text-muted-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <template v-for="group in groups" :key="group.investment.id">
              <!-- Investment Subheader -->
              <TableRow class="bg-muted/50 hover:bg-muted/80 border-b border-border transition-colors">
                <TableCell colspan="4" class="py-4">
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 bg-zinc-900 border border-zinc-800 rounded-lg flex items-center justify-center text-white shadow-lg">
                      <Briefcase class="w-4 h-4" />
                    </div>
                    <span class="font-black text-foreground tracking-tight">{{ group.investment.name }}</span>
                    <Badge variant="outline" class="ml-2 bg-muted/30 text-muted-foreground border-border text-[10px] font-bold">
                      {{ group.movements.length }} flux
                    </Badge>
                  </div>
                </TableCell>
              </TableRow>

              <!-- Movements -->
              <TableRow v-for="m in group.movements" :key="m.id" class="group border-b border-border/30 last:border-0 hover:bg-muted/10 transition-colors">
                <TableCell>
                  <div class="flex flex-col">
                    <span class="font-black text-red-400 tabular-nums flex items-center gap-1.5 antialiased">
                      <Calendar class="w-3.5 h-3.5 mb-0.5" /> {{ formatDateFr(m.date) }}
                    </span>
                    <span class="text-[10px] text-muted-foreground/50 font-extrabold uppercase tracking-tight ml-5">Date échue</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div class="flex items-center gap-2">
                    <Badge :class="[m.type === 'payment' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20', 'text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded shadow-sm']">
                      {{ m.type === 'payment' ? 'Versement' : 'Retrait' }}
                    </Badge>
                    <span class="text-xs font-bold text-muted-foreground italic">
                      {{ (m.subMovements && m.subMovements.length > 0) ? 'Composé' : (m.category?.name || '-') }}
                    </span>
                  </div>
                </TableCell>
                <TableCell class="text-right">
                  <span class="text-lg font-black tabular-nums tracking-tighter" :class="[m.type === 'payment' ? 'text-emerald-400' : 'text-red-400', { 'blur-md select-none': isAmountsHidden }]">
                    {{ m.type === 'payment' ? '+' : '-' }}{{ formatAmount(m.amount) }}€
                  </span>
                </TableCell>
                <TableCell class="text-right">
                  <div class="flex items-center justify-end gap-2">
                    <Button variant="finance" size="sm" class="h-8 font-black text-[10px] uppercase shadow-lg shadow-black/20" 
                            @click="validateMovement(m.id)" :disabled="validatingId === m.id"
                            :aria-label="`Valider le mouvement du ${formatDateFr(m.date)} pour ${group.investment.name}`">
                      <RefreshCw v-if="validatingId === m.id" class="w-3 h-3 animate-spin mr-1.5" aria-hidden="true" />
                      <Check v-else class="w-3 h-3 mr-1.5" aria-hidden="true" />
                      Valider
                    </Button>
                    <Button variant="ghost" size="icon" title="Voir le placement" class="h-8 w-8 hover:bg-muted text-muted-foreground hover:text-foreground focus-visible:ring-1 focus-visible:ring-indigo-500" 
                            @click="router.push({ name: 'finance_investment_detail', params: { id: m.investment.id, name: slugify(m.investment.name) } })"
                            aria-label="Voir le détail du placement">
                      <ArrowRight class="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </template>
          </TableBody>
        </Table>
      </div>
    </div>

    <ValuationModal 
        v-if="showValuationModal"
        :show="showValuationModal"
        :investment="selectedInvestment"
        :movementCategories="movementCategories"
        :rawMovementCategories="rawMovementCategories"
        @close="showValuationModal = false"
        @saved="onValuationSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import { 
  CheckSquare, History, ArrowRight, RefreshCw, 
  Smile, Briefcase, Calendar, Check, Bell 
} from 'lucide-vue-next'

// Utilities
import { formatAmount, formatDateFr, slugify } from '@/lib/utils'
import { useGlobalState } from '@/composables/useGlobalState'

// UI Components
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell 
} from '@/components/ui/table'

const router = useRouter()
const isAmountsHidden = inject('isAmountsHidden')
const movements = ref([])
const subscriptionAlerts = ref([])
const investments = ref([])
const rawMovementCategories = ref([])
const loading = ref(true)
const validatingId = ref(null)

const showValuationModal = ref(false)
const selectedInvestment = ref(null)

const { getAuthHeader } = useGlobalState()

const fetchPendingMovements = async () => {
    loading.value = true
    try {
        const [movRes, invRes, catRes, subRes] = await Promise.all([
            fetch('/api/movements/pending', { headers: getAuthHeader() }),
            fetch('/api/investments', { headers: getAuthHeader() }),
            fetch('/api/movements/categories', { headers: getAuthHeader() }),
            fetch('/api/subscriptions/pending-alerts', { headers: getAuthHeader() })
        ])
        
        if (movRes.ok) movements.value = await movRes.json()
        if (invRes.ok) investments.value = await invRes.json()
        if (catRes.ok) rawMovementCategories.value = await catRes.json()
        if (subRes.ok) subscriptionAlerts.value = await subRes.json()

        // Force refresh of the sidebar count by emitting an event
        window.dispatchEvent(new Event('finance-pending-updated'))
    } catch (e) {
        console.error("Failed fetching pending data", e)
    } finally {
        loading.value = false
    }
}

const validateMovement = async (id) => {
    validatingId.value = id
    try {
        const res = await fetch(`/api/movements/${id}/validate`, { method: 'POST', headers: getAuthHeader() })
        if (res.ok) {
            // Remove locally for immediate UI feedback
            movements.value = movements.value.filter(m => m.id !== id)
            // Emit event to update sidebar count instantly
            window.dispatchEvent(new Event('finance-pending-updated'))
        } else {
            alert("Erreur lors de la validation")
        }
    } catch (e) {
        console.error(e)
    } finally {
        if (validatingId.value === id) validatingId.value = null
    }
}

const validateSubscription = async (id) => {
    validatingId.value = 'sub_' + id
    try {
        const res = await fetch(`/api/subscriptions/${id}/validate`, { method: 'POST', headers: getAuthHeader() })
        if (res.ok) {
            subscriptionAlerts.value = subscriptionAlerts.value.filter(s => s.id !== id)
        } else {
            alert("Erreur lors de la validation de l'abonnement")
        }
    } catch (e) {
        console.error(e)
    } finally {
        if (validatingId.value === 'sub_' + id) validatingId.value = null
    }
}

const groups = computed(() => {
    const grps = {}
    movements.value.forEach(m => {
        const invId = m.investment.id
        if (!grps[invId]) {
            grps[invId] = {
                investment: m.investment,
                movements: []
            }
        }
        grps[invId].movements.push(m)
    })
    return Object.values(grps).sort((a,b) => a.investment.name.localeCompare(b.investment.name))
})

const movementCategories = computed(() => {
    const buildTree = (list, parentId = null, depth = 0) => {
        let tree = []
        list.filter(c => c.parentId === parentId).forEach(c => {
            tree.push({ ...c, displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (depth > 0 ? '└ ' : '') + c.name })
            tree = tree.concat(buildTree(list, c.id, depth + 1))
        })
        return tree
    }
    return buildTree(rawMovementCategories.value)
})

const valuationAlerts = computed(() => {
    const threeMonthsAgo = new Date()
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
    
    return investments.value.filter(inv => {
        const hasRate = parseFloat(inv.expectedReturnRate || 0) > 0
        if (!hasRate) return false
        
        if (!inv.lastValuationUpdate) return true
        const lastUpdate = new Date(inv.lastValuationUpdate)
        return lastUpdate < threeMonthsAgo
    })
})

const openValuationModal = (inv) => {
    selectedInvestment.value = inv
    showValuationModal.value = true
}

const onValuationSaved = () => {
    showValuationModal.value = false
    fetchPendingMovements()
}

onMounted(() => {
    fetchPendingMovements()
})
import ValuationModal from './Modals/ValuationModal.vue'
</script>

<style scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
